import { useEffect, useState } from 'react';
import { fetchRegistrableExpansions } from '../api/api';
import { RegistrableGameType } from '../types/productRegistration';
import { CardType } from '../types/card';
import type { FilterOption } from './UseCardView';

const REGISTRABLE_TYPES: RegistrableGameType[] = ['POKEMON', 'ONE_PIECE', 'DIGIMON', 'HOLOLIVE'];

function isRegistrable(gameType: CardType | 'ALL'): gameType is RegistrableGameType {
  return (REGISTRABLE_TYPES as string[]).includes(gameType);
}

/**
 * 확장팩 필터 옵션의 출처 — pricehub 카탈로그를 지원하는 게임(포켓몬/원피스/디지몬/
 * 홀로라이브)은 그 카탈로그 전체(발매일·카드수 포함, 아직 이 매장에 등록 안 된 팩도
 * 나온다 — "모든 카드 정보는 pricehub에서 가져오는 게 맞다"는 2026-08-27 피드백 반영)를
 * 쓰고, pricehub 카탈로그가 없는 나머지(게임 미선택 '전체', 위즈 슈바르츠·쿠키런·기타)는
 * 이 매장에 실제 등록된 카드에서 뽑은 로컬 목록(localOptions, useCardFilters의
 * expansionOptions)으로 되돌아간다.
 */
export function useExpansionCatalog(
  gameType: CardType | 'ALL',
  localOptions: FilterOption[],
): { options: FilterOption[]; loading: boolean } {
  const [catalog, setCatalog] = useState<FilterOption[] | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isRegistrable(gameType)) {
      setCatalog(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setCatalog(null);
    fetchRegistrableExpansions(gameType)
      .then((list) => {
        if (cancelled) return;
        setCatalog(list.map((e) => ({
          value: e.code,
          label: e.nameKr || e.name,
          count: e.cardCount,
          releaseDate: e.releaseDate,
          productType: e.productType,
        })));
      })
      .catch(() => { if (!cancelled) setCatalog(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [gameType]);

  if (isRegistrable(gameType)) return { options: catalog ?? [], loading };
  return { options: localOptions, loading: false };
}
